import { Box, BoxProps, Tooltip } from "@mui/material";
import React from "react";
import { SemanticIndicatorColors } from "@/styles/nawy-theme/foundation/colors/semantic";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { SxProps } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { iconSizes, sizeStyles } from "./constants";

export type StatusVariant = "primary" | "secondary";
export type StatusSize = "lg" | "md" | "sm";

export interface StatusProps
  extends Omit<BoxProps, "variant" | "color" | "size"> {
  label: string | React.ReactNode;
  variant?: StatusVariant;
  color?: SemanticIndicatorColors;
  bgHexColor?: string;
  size?: StatusSize;
  width?: string;
  onClick?: () => void;
  textStyle?: SxProps;
}

// Status variant styles
const getStatusStyles = (
  variant: StatusVariant = "primary",
  color: SemanticIndicatorColors = "blue",
  bgHexColor?: string,
  size: StatusSize = "md",
  clickable: boolean = false,
  width?: string
): SxProps => {
  // Base styles that are common to all variants
  const baseStyles: SxProps = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-start",
    borderRadius: nawyTheme.nawy.radius.md,
    fontFamily: "inherit",
    transition: "all 0.2s ease-in-out",
    width: width || "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    ...(sizeStyles.get(size) || sizeStyles.get("md")),
  };

  // Determine background and text colors
  let backgroundColor: string;
  let textColor: string;
  const safeColor =
    bgHexColor !== "null" && bgHexColor !== undefined && bgHexColor !== null
      ? bgHexColor
      : "#e5e7eb";

  if (bgHexColor) {
    backgroundColor = safeColor;
    textColor = nawyTheme.nawy.colors.primitives.white[100] as string;
  } else {
    // Use semantic colors
    const semanticColor = nawyTheme.nawy.colors.semantic.indicator[
      color
    ] as string;
    const semanticColorLight = nawyTheme.nawy.colors.semantic.indicator[
      `${color}Light` as keyof typeof nawyTheme.nawy.colors.semantic.indicator
    ] as string;
    const semanticColorDark = nawyTheme.nawy.colors.semantic.indicator[
      `${color}Dark` as keyof typeof nawyTheme.nawy.colors.semantic.indicator
    ] as string;

    if (variant === "primary") {
      backgroundColor = semanticColor || "#000000";
      textColor =
        (nawyTheme.nawy.colors.semantic.text.onColor as string) || "#ffffff";
    } else {
      backgroundColor = semanticColorLight || "#f0f0f0";
      textColor = semanticColorDark || "#000000";
    }
  }

  // Apply colors to base styles
  const statusStyles = {
    ...baseStyles,
    backgroundColor,
    color: textColor,
    border: "none",
  };

  // Add clickable styles if onClick is provided
  if (clickable) {
    return {
      ...statusStyles,
      cursor: "pointer",
      userSelect: "none",
      "&:hover": {
        opacity: 0.8,
        transform: "translateY(-1px)",
      },
      "&:active": {
        transform: "translateY(0px)",
        opacity: 0.9,
      },
    };
  }

  return statusStyles;
};

export const Status: React.FC<StatusProps> = ({
  label,
  variant = "primary",
  color = "blue",
  bgHexColor,
  size = "md",
  width,
  onClick,
  textStyle,
  ...props
}) => {
  const labelRef = React.useRef<HTMLSpanElement | null>(null);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const stringLabel = typeof label === "string" ? label : "";

  React.useEffect(() => {
    const element = labelRef.current;

    if (!element || !stringLabel) {
      setShowTooltip(false);
      return;
    }

    const checkOverflow = () => {
      setShowTooltip(element.scrollWidth > element.clientWidth);
    };

    checkOverflow();

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    }

    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [stringLabel]);

  const handleClick = (event: React.MouseEvent) => {
    if (onClick) {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Box
      component={onClick ? "button" : "span"}
      sx={getStatusStyles(variant, color, bgHexColor, size, !!onClick, width)}
      onClick={handleClick}
      {...props}
    >
      <Tooltip
        disableHoverListener={!showTooltip}
        title={stringLabel}
        placement="top"
      >
        <Box
          ref={labelRef}
          component="span"
          sx={{
            fontWeight: "bold",
            lineHeight: 1.2,
            fontSize: "clamp(10px, 0.95vw, 12px)",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: "1 1 auto",
            maxWidth: "100%",
            ...textStyle,
          }}
        >
          {label}
        </Box>
      </Tooltip>
      {onClick && (
        <KeyboardArrowDownIcon
          sx={{
            fontSize: iconSizes.get(size) || iconSizes.get("md"),
            marginLeft: nawyTheme.nawy.spacing.xxs,
            flexShrink: 0,
          }}
        />
      )}
    </Box>
  );
};

export default Status;
